import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  imageAssetGuidePages,
} from "../data/imageAssetGuide";
import { getPublicStorageUrl, supabase, isSupabaseConfigured } from "../lib/supabaseClient";

const BUCKET = "site-images";

export default function AdminImages() {
  const [prefix, setPrefix] = useState("");
  const [objects, setObjects] = useState([]);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const refreshList = useCallback(async () => {
    if (!supabase) return;
    setBusy(true);
    const path = prefix.replace(/^\/+/, "");
    const { data, error } = await supabase.storage.from(BUCKET).list(path, {
      limit: 200,
      sortBy: { column: "name", order: "asc" },
    });
    setBusy(false);
    if (error) {
      setMsg({ type: "danger", text: error.message });
      setObjects([]);
      return;
    }
    setObjects(data || []);
    setMsg(null);
  }, [prefix]);

  useEffect(() => {
    if (isSupabaseConfigured) refreshList();
  }, [refreshList]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !supabase) return;
    const fileName = prompt(
      "Object path inside bucket (e.g. img/banner/banner.png):",
      prefix ? `${prefix.replace(/\/$/, "")}/${file.name}` : file.name
    );
    if (!fileName) return;
    setBusy(true);
    const { error } = await supabase.storage
      .from(BUCKET)
      .upload(fileName.replace(/^\/+/, ""), file, {
        upsert: true,
        contentType: file.type || undefined,
      });
    setBusy(false);
    if (error) {
      setMsg({ type: "danger", text: error.message });
      return;
    }
    setMsg({
      type: "success",
      text: `Uploaded. Public URL: ${getPublicStorageUrl(fileName) || fileName}`,
    });
    refreshList();
  };

  if (!isSupabaseConfigured) {
    return (
      <p className="text-danger">Supabase is not configured. Add keys to .env</p>
    );
  }

  return (
    <div>
      <h1 className="h3 mb-3">Images</h1>
      <p className="text-muted small">
        Files here are served from the public bucket{" "}
        <code>{BUCKET}</code>. Use the URLs in CMS JSON (for example{" "}
        <code>news_list</code> image fields).
      </p>

      <div className="alert alert-light border small">
        <strong>Local checklist:</strong> filenames for bundled assets are still
        documented on{" "}
        <Link to="/image-guide">Image asset guide</Link> (dev upload script).
        Supabase is best for <strong>CMS-driven</strong> images.
      </div>

      <div className="row g-3 mb-4">
        <div className="col-md-6">
          <label className="form-label small text-muted">List folder prefix</label>
          <div className="input-group input-group-sm">
            <input
              type="text"
              className="form-control"
              placeholder="e.g. img/banner"
              value={prefix}
              onChange={(e) => setPrefix(e.target.value)}
            />
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={refreshList}
              disabled={busy}
            >
              List
            </button>
          </div>
        </div>
        <div className="col-md-6 d-flex align-items-end">
          <div>
            <input
              id="admin-img-upload"
              type="file"
              accept="image/*"
              className="d-none"
              onChange={onUpload}
              disabled={busy}
            />
            <label htmlFor="admin-img-upload" className="btn btn_theme btn_sm mb-0">
              Upload to bucket…
            </label>
          </div>
        </div>
      </div>

      {msg && (
        <div className={`alert alert-${msg.type} small`}>{msg.text}</div>
      )}

      <h2 className="h6 text-muted">Objects {prefix ? `in ${prefix}/` : "at root"}</h2>
      <div className="table-responsive bg-white rounded shadow-sm">
        <table className="table table-sm align-middle mb-0">
          <thead className="table-light">
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Public URL</th>
            </tr>
          </thead>
          <tbody>
            {objects.length === 0 && (
              <tr>
                <td colSpan={3} className="text-muted small">
                  {busy ? "Loading…" : "No objects (or folder empty)."}
                </td>
              </tr>
            )}
            {objects.map((o) => {
              const fullPath = prefix
                ? `${prefix.replace(/\/$/, "")}/${o.name}`
                : o.name;
              const isFolder = !o.metadata && o.id === null;
              const url = !isFolder ? getPublicStorageUrl(fullPath) : null;
              return (
                <tr key={fullPath}>
                  <td>
                    <code>{o.name}</code>
                    {isFolder && (
                      <span className="text-muted small ms-1">(folder)</span>
                    )}
                  </td>
                  <td className="small">
                    {o.metadata?.mimetype || (isFolder ? "—" : "file")}
                  </td>
                  <td className="small">
                    {url ? (
                      <a href={url} target="_blank" rel="noreferrer">
                        open
                      </a>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <h2 className="h6 text-muted mt-5">Recommended filenames (checklist)</h2>
      <p className="small text-muted">
        Reference from the asset guide — upload matching paths to Storage if
        you use CMS URLs.
      </p>
      <div className="bg-white rounded border p-2">
        {imageAssetGuidePages.map((page) => (
          <details key={page.id} className="mb-2">
            <summary className="small fw-bold">{page.title}</summary>
            <ul className="mb-0 mt-2 small">
              {page.assets.map((a, i) => (
                <li key={`${a.file}-${i}`}>
                  <code>
                    {a.subfolder ? `${a.subfolder}/` : ""}
                    {a.file}
                  </code>
                  <span className="text-muted"> — {a.dims}</span>
                </li>
              ))}
            </ul>
          </details>
        ))}
      </div>
    </div>
  );
}
